import React from "react";
import { Download } from "lucide-react";

export type TransactionTone = "paid" | "failed" | "pending";

const PILL: Record<TransactionTone, { label: string; classes: string }> = {
  paid: { label: "PAID", classes: "bg-brand-soft text-brand-dark" },
  failed: { label: "NOT PAID", classes: "bg-[#FDECEC] text-[#D14343]" },
  pending: { label: "PENDING", classes: "bg-[#FFF6E5] text-[#9A6700]" },
};

export interface TransactionRow {
  label: string;
  /** Left off entirely where the answer is not known — see below. */
  value?: string | null;
  /** Money that came off rather than money that was taken — the coupon line,
   *  which is the one figure on a receipt that is not a charge. */
  credit?: boolean;
  /**
   * Fetching the document this row names, as an icon at the end of it.
   *
   * On the invoice row, which is the only row of a receipt that names
   * something there is a copy of. It is drawn here rather than as a button
   * under the card because a receipt already prints the invoice number, and
   * the thing to press is the number's own line — the same shape the Billing
   * tab's history gives it, where every invoice is a row and the download is
   * the last cell of it.
   */
  download?: {
    /** The accessible name: the glyph alone says "download" and not what of. */
    label: string;
    onClick: () => void;
    /** While the bytes are on their way — the row spins rather than the page. */
    busy?: boolean;
  };
}

interface TransactionDetailsProps {
  rows: TransactionRow[];
  tone: TransactionTone;
}

/**
 * The receipt: what was bought, for how much, by what, and when it renews.
 *
 * A row with no value is dropped rather than drawn with a dash. On a receipt a
 * dash is not "we do not know yet" — it reads as a field that is genuinely
 * empty, which for an invoice number or a payment method is a different and
 * more alarming claim. What the screen cannot say, it does not say. That is
 * also what lets one card serve a payment taken in test mode, where there is
 * no invoice number to print, and a real one, where there is.
 */
const TransactionDetails: React.FC<TransactionDetailsProps> = ({
  rows,
  tone,
}) => {
  const known = rows.filter((row) => !!row.value);
  const pill = PILL[tone];

  return (
    <section
      aria-labelledby="transaction-details-heading"
      className="mt-8 card-surface border border-line bg-white text-left"
    >
      <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-4 3xl:px-6">
        <h3
          id="transaction-details-heading"
          className="text-[15px] 3xl:text-[16px] font-medium text-ink"
        >
          Transaction details
        </h3>
        <span
          className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] 3xl:text-[12px] font-semibold tracking-wide ${pill.classes}`}
        >
          {pill.label}
        </span>
      </div>

      <dl className="flex flex-col gap-3 px-5 py-4 3xl:px-6 3xl:py-5">
        {known.map((row) => (
          <div
            key={row.label}
            className="flex items-baseline justify-between gap-4"
          >
            <dt className="text-[13px] 3xl:text-[14px] text-ink-muted">
              {row.label}
            </dt>
            {/* Tabular figures so amounts, dates and reference numbers line up
                down the right edge rather than wandering by a pixel each. */}
            <dd
              className={`flex items-center justify-end gap-1.5 tabular-nums text-right text-[13px] 3xl:text-[14px] font-medium ${
                row.credit ? "text-[#00A86B]" : "text-ink"
              }`}
            >
              <span>{row.value}</span>
              {row.download && (
                // Pulled back into the card's own padding and out of the
                // row's rhythm, so a line that carries a button sits at the
                // same height as the lines above and below it and its value
                // still ends on the same right edge as theirs.
                <button
                  type="button"
                  onClick={row.download.onClick}
                  disabled={row.download.busy}
                  aria-label={row.download.label}
                  className="-my-1.5 -mr-1.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-brand transition-colors hover:bg-brand-soft disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {row.download.busy ? (
                    <span
                      aria-hidden="true"
                      className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                    />
                  ) : (
                    // The same glyph the history table's own download wears:
                    // one action, one icon, wherever an invoice is listed.
                    <Download aria-hidden="true" className="h-4 w-4" />
                  )}
                </button>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export default TransactionDetails;
